import { useCallback } from 'react';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { noop } from 'utils/noop';
import { analytics } from 'utils/analytics';
import { Pack } from 'types/generated/graphql';
import { SampleType } from 'types/generated/graphql';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';

export function useAddSessionToCreator(
    sessionGuid?: string,
    interactionSource?: string,
): {
    addToCreator: ({ withAutoplay }: { withAutoplay?: boolean }) => void;
} {
    const { isDAWPlugin } = useExecutionEnvironmentContext();
    const {
        loadSessionByGuid,
        state: {
            samples,
            keyRoot,
            keyQuality,
            keySign,
            bpm,
            instrumentationSessionId,
        },
    } = useCreatorContext();

    const addToCreatorCallback = useCallback(
        async ({ withAutoplay }: { withAutoplay?: boolean }) => {
            if (sessionGuid) {
                await loadSessionByGuid({
                    sessionGuid,
                    isAutoload: !!interactionSource,
                    withAutoplay,
                });

                samples.forEach((sample) => {
                    analytics.trackCreatorUpdated({
                        creatorBpm: bpm,
                        creatorKey: `${keyRoot} ${keyQuality} - ${keySign}`,
                        deletedCreatorSession: false,
                        numberSamplesInCreator: samples.size || 0,
                        sampleAdded: true,
                        sampleRemoved: false,
                        creatorSessionId: instrumentationSessionId,
                        samplePackId: sample.pack?.guid,
                        samplePackName: sample.pack?.name,
                        assetArtist: sample.pack?.artist?.guid,
                        assetBpm: sample.bpm,
                        assetId: sample.guid,
                        assetKey: sample.key?.code,
                        assetLength: sample.duration || undefined,
                        assetName: sample.name,
                        assetGenre: (sample.pack as Pack)?.mainGenre?.code,
                        assetLabel:
                            (sample.pack as Pack)?.label?.guid || undefined,
                        assetLoopType: sample.sampleType as SampleType,
                    });
                });
            }
        },
        [
            sessionGuid,
            loadSessionByGuid,
            interactionSource,
            samples,
            keyRoot,
            keyQuality,
            keySign,
            bpm,
            instrumentationSessionId,
        ],
    );

    if (isDAWPlugin || !sessionGuid) {
        return { addToCreator: noop };
    }

    return { addToCreator: addToCreatorCallback };
}
